<template>
  <div class="about">
    <my-navi activeIndex="20"></my-navi>
    <el-container>
      <el-header>
        <span style="font-weight: bold; font-size: 16px;"
          >亚马逊商品价格检查工具</span
        >
      </el-header>
      <el-main>
        <el-card>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="功能说明">
              <ol>
                <li>
                  <strong>粘贴页面内容</strong> —
                  打开亚马逊商品页面，全选复制后粘贴到下方文本框
                </li>
                <li>
                  <strong>AI分析</strong> —
                  后端AI分析页面内容，提取商品各配置型号及其价格
                </li>
                <li>
                  <strong>价格比对</strong> —
                  根据内存和硬盘的阶梯价格规则，检查各配置定价是否合理
                </li>
              </ol>
            </el-descriptions-item>
            <el-descriptions-item label="价格比对规则">
              <ul>
                <li>
                  <strong>内存规则：</strong>DDR4每增加4GB价格上涨$40，DDR5每增加4GB价格上涨$60
                </li>
                <li>
                  <strong>硬盘阶梯：</strong>
                  256GB→512GB +$30 | 512GB→1TB +$100 | 1TB→2TB +$120 |
                  2TB→4TB +$180 | 4TB→6TB +$220 | 6TB→8TB +$180
                </li>
                <li v-if="site === 'CA'">
                  <strong>CA计算公式：</strong>(价格梯度 + 10) × 1.42
                </li>
              </ul>
            </el-descriptions-item>
          </el-descriptions>

          <el-card style="margin-top: 20px;" shadow="never">
            <div slot="header">
              <span style="font-weight: bold;">页面内容（必填）</span>
              <el-radio-group
                v-model="site"
                size="small"
                style="margin-left: 20px;"
                @change="onSiteChange"
              >
                <el-radio-button label="US">US (USD)</el-radio-button>
                <el-radio-button label="CA">CA (CAD)</el-radio-button>
              </el-radio-group>
              <el-button
                type="primary"
                style="float: right;"
                @click="analyzeProduct"
                :loading="analyzing"
                :disabled="!pageContent.trim() || analyzing"
              >
                <i class="el-icon-search"></i>
                AI分析
              </el-button>
              <el-button
                style="float: right; margin-right: 10px;"
                @click="resetAll"
                :disabled="analyzing"
                >重置</el-button
              >
            </div>
            <el-alert
              title="如何获取页面内容"
              type="info"
              :closable="false"
              show-icon
              style="margin-bottom: 15px;"
            >
              <template slot="default">
                <ol style="margin: 5px 0 0 0; padding-left: 20px; line-height: 1.8;">
                  <li>在浏览器中打开亚马逊商品页面（如商品详情页或变体对比页）</li>
                  <li>按 <kbd>Ctrl+A</kbd> 全选页面所有内容</li>
                  <li>按 <kbd>Ctrl+C</kbd> 复制选中的内容</li>
                  <li>将复制的内容粘贴到下方文本框中</li>
                </ol>
                <p style="margin: 8px 0 0 0; color: #909399; font-size: 12px;">
                  提示：粘贴的内容应包含各配置型号、内存、硬盘和价格信息，AI将从中提取数据进行分析。
                </p>
              </template>
            </el-alert>
            <el-input
              v-model="pageContent"
              type="textarea"
              :rows="10"
              placeholder="在此粘贴亚马逊商品页面的内容..."
            ></el-input>
          </el-card>

          <el-row
            v-if="analyzing"
            style="margin-top: 20px;"
            type="flex"
            justify="center"
          >
            <el-card style="width: 100%; text-align: center;">
              <i class="el-icon-loading" style="font-size: 24px;"></i>
              <p style="margin-top: 10px; color: #909399;">
                {{ statusMessage }}
              </p>
            </el-card>
          </el-row>

          <el-row
            v-if="productData.length > 0"
            style="margin-top: 20px;"
            :gutter="20"
          >
            <el-col :span="14">
              <el-card>
                <div slot="header">
                  <span>商品配置与价格</span>
                  <span style="float: right; color: #909399;"
                    >共 {{ productData.length }} 个配置</span
                  >
                </div>
                <el-table
                  :data="productData"
                  border
                  style="width: 100%; font-size: 12px;"
                >
                  <el-table-column
                    label="型号/SKU"
                    prop="name"
                    min-width="200px"
                    show-overflow-tooltip
                  />
                  <el-table-column
                    label="内存类型"
                    prop="memoryType"
                    width="100px"
                  />
                  <el-table-column
                    label="内存容量"
                    prop="memorySize"
                    width="100px"
                  />
                  <el-table-column
                    label="硬盘容量"
                    width="100px"
                  >
                    <template slot-scope="scope">
                      {{ formatStorage(scope.row.storageSize) }}
                    </template>
                  </el-table-column>
                  <el-table-column
                    :label="`页面价格(${currencySymbol})`"
                    prop="price"
                    width="120px"
                  >
                    <template slot-scope="scope">
                      <span style="font-weight: bold;"
                        >{{ currencySymbol }}{{ scope.row.price }}</span
                      >
                    </template>
                  </el-table-column>
                </el-table>
              </el-card>
            </el-col>
            <el-col :span="10">
              <el-card>
                <div slot="header">
                  <span>价格比对结果</span>
                </div>
                <el-table
                  :data="comparisonResults"
                  border
                  style="width: 100%; font-size: 12px;"
                >
                  <el-table-column
                    label="对比项"
                    prop="label"
                    min-width="180px"
                    show-overflow-tooltip
                  />
                  <el-table-column label="页面价" prop="actualDiff" width="100px">
                    <template slot-scope="scope">
                      <span>{{ currencySymbol }}{{ scope.row.actualDiff }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="应价差" prop="expectedDiff" width="100px">
                    <template slot-scope="scope">
                      <el-tooltip
                        v-if="scope.row.formula"
                        effect="dark"
                        placement="top"
                      >
                        <div slot="content" style="white-space: pre-line; line-height: 1.6;">{{ scope.row.formula }}</div>
                        <span style="cursor: help; border-bottom: 1px dashed #909399;">{{ currencySymbol }}{{ scope.row.expectedDiff }}</span>
                      </el-tooltip>
                      <span v-else>{{ currencySymbol }}{{ scope.row.expectedDiff }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="差异" prop="deviation" width="100px">
                    <template slot-scope="scope">
                      <span
                        :style="{
                          color:
                            Math.abs(scope.row.deviation) > (20 * exchangeRate)
                              ? 'red'
                              : Math.abs(scope.row.deviation) > (10 * exchangeRate)
                                ? '#E6A23C'
                                : '#67C23A',
                          fontWeight: 'bold'
                        }"
                      >
                        {{ scope.row.deviation > 0 ? '+' : ""
                        }}{{ currencySymbol }}{{ scope.row.deviation }}
                      </span>
                    </template>
                  </el-table-column>
                </el-table>
                <div style="margin-top: 15px; color: #909399; font-size: 12px;">
                  <p>说明：以最低配置为基准，计算每个配置相对于基准的实际价差和应有价差。</p>
                  <p>
                    <span style="color: #67C23A;">绿色</span>
                    差异≤{{ currencySymbol }}{{ (10 * exchangeRate).toFixed(0) }} |
                    <span style="color: #E6A23C;">黄色</span>
                    差异{{ currencySymbol }}{{ (10 * exchangeRate).toFixed(0) }}~{{ currencySymbol }}{{ (20 * exchangeRate).toFixed(0) }} |
                    <span style="color: red;">红色</span>
                    差异>{{ currencySymbol }}{{ (20 * exchangeRate).toFixed(0) }}
                  </p>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <el-row
            v-if="aiRawResponse"
            style="margin-top: 20px;"
          >
            <el-card>
              <div slot="header">
                <span>AI原始响应</span>
                <el-button
                  type="text"
                  style="float: right;"
                  @click="aiRawResponse = ''"
                  >收起</el-button
                >
              </div>
              <pre style="white-space: pre-wrap; font-size: 12px; max-height: 400px; overflow-y: auto;">{{ aiRawResponse }}</pre>
            </el-card>
          </el-row>
        </el-card>
      </el-main>
    </el-container>
  </div>
</template>

<script>
import axios from "axios";

const BACKEND_API = "https://wtodland.store/api";

export default {
  name: "PriceCheckAI",
  data() {
    return {
      pageContent: "",
      site: "US",
      analyzing: false,
      statusMessage: "",
      productData: [],
      comparisonResults: [],
      aiRawResponse: ""
    };
  },
  computed: {
    exchangeRate() {
      return this.site === "CA" ? 1.42 : 1;
    },
    currencySymbol() {
      return this.site === "CA" ? "C$" : "$";
    }
  },
  methods: {
    resetAll() {
      this.pageContent = "";
      this.productData = [];
      this.comparisonResults = [];
      this.aiRawResponse = "";
      this.statusMessage = "";
    },

    onSiteChange() {
      // 切换站点时重新计算价格比对
      if (this.productData.length > 1) {
        this.comparePrices();
      }
    },

    async analyzeProduct() {
      const content = this.pageContent.trim();
      if (!content) {
        this.$message.warning("请粘贴商品页面内容");
        return;
      }

      this.analyzing = true;
      this.productData = [];
      this.comparisonResults = [];
      this.aiRawResponse = "";

      try {
        this.statusMessage = "正在提交后端AI分析...";

        const response = await axios.post(
          `${BACKEND_API}/fetch-page`,
          { content: content },
          {
            headers: { "Content-Type": "application/json" },
            timeout: 120000
          }
        );

        const result = response.data;
        this.aiRawResponse = JSON.stringify(result, null, 2);

        // 解析后端返回的分析结果
        this.statusMessage = "正在解析分析结果...";
        this.parseAIResult(result);

        // 价格比对
        if (this.productData.length > 0) {
          this.statusMessage = "正在进行价格比对...";
          this.comparePrices();
        }

        this.$message.success("分析完成");
      } catch (error) {
        this.$message.error("分析失败：" + (error.message || "未知错误"));
      } finally {
        this.analyzing = false;
        this.statusMessage = "";
      }
    },

    parseAIResult(result) {
      try {
        let data = result;
        // 如果后端返回字符串，尝试解析JSON
        if (typeof result === "string") {
          const jsonMatch = result.match(/\{[\s\S]*\}/);
          if (!jsonMatch) {
            throw new Error("返回结果中未找到有效JSON");
          }
          data = JSON.parse(jsonMatch[0]);
        }

        if (data.variants && Array.isArray(data.variants)) {
          this.productData = data.variants.map(v => ({
            name: v.name || "",
            memoryType: v.memoryType || "DDR4",
            memorySize: parseInt(v.memorySize) || 0,
            storageSize: parseInt(v.storageSize) || 0,
            price: parseFloat(v.price) || 0
          }));
        } else {
          throw new Error("返回数据格式不正确，缺少variants数组");
        }
      } catch (e) {
        this.$message.error("解析结果失败：" + e.message);
      }
    },

    comparePrices() {
      if (this.productData.length < 2) {
        this.$message.info("只有1个配置，无需比对");
        return;
      }

      // 按价格排序，找最低配置作为基准
      const sorted = [...this.productData].sort(
        (a, b) => a.price - b.price
      );
      const base = sorted[0];

      // 计算每个配置相对于基准的应有价差
      this.comparisonResults = [];

      for (let i = 0; i < sorted.length; i++) {
        const item = sorted[i];
        // 跳过基准配置自身
        if (i === 0) {
          this.comparisonResults.push({
            label: item.name + " (基准)",
            actualDiff: item.price.toFixed(2),
            expectedDiff: "0.00",
            deviation: 0
          });
          continue;
        }

        // 计算内存价差（USD基准价 × 汇率）
        const memoryDiffUsd = this.calcMemoryDiff(
          base.memoryType,
          base.memorySize,
          item.memoryType,
          item.memorySize
        );

        // 计算硬盘价差（USD基准价 × 汇率）
        const storageDiffUsd = this.calcStorageDiff(
          base.storageSize,
          item.storageSize
        );

        const expectedDiff = (memoryDiffUsd + storageDiffUsd + 10) * this.exchangeRate;
        const actualDiff = item.price - base.price;
        const deviation = Math.round((actualDiff - expectedDiff) * 100) / 100;

        // 生成计算公式描述
        let formula = "";
        const memParts = [];
        const storageParts = [];
        if (memoryDiffUsd > 0 || storageDiffUsd > 0) {
          const gradientUsd = memoryDiffUsd + storageDiffUsd;
          const gradientCad = gradientUsd * this.exchangeRate;
          if (this.site === 'CA') {
            memParts.push(
              `梯度合计: ${gradientUsd} + 10 = ${gradientUsd + 10} × 1.42 = ${this.currencySymbol}${expectedDiff.toFixed(0)}`
            );
          } else {
            memParts.push(
              `梯度合计: ${gradientUsd} + 10 = ${this.currencySymbol}${(gradientUsd + 10).toFixed(0)}`
            );
          }
          if (memoryDiffUsd > 0) {
            const memType = item.memoryType || "DDR4";
            const unitPriceUsd = memType === "DDR5" ? 60 : 40;
            memParts.push(
              `  内存: (${item.memorySize}-${base.memorySize})GB / 4 × $${unitPriceUsd} (${memType}) = $${memoryDiffUsd}`
            );
          }
          if (storageDiffUsd > 0) {
            storageParts.push(
              `  硬盘: ${this.formatStorage(base.storageSize)} → ${this.formatStorage(item.storageSize)} = $${storageDiffUsd}`
            );
          }
        }
        formula = [memParts.join("\n"), storageParts.join("\n")]
          .filter(Boolean)
          .join("\n");
        if (!formula) formula = "无配置差异";

        this.comparisonResults.push({
          label: item.name,
          actualDiff: actualDiff.toFixed(2),
          expectedDiff: expectedDiff.toFixed(2),
          deviation: deviation,
          formula: formula
        });
      }
    },

    calcMemoryDiff(baseType, baseSize, targetType, targetSize) {
      // 以目标配置的内存类型确定单价
      const pricePer4GB =
        targetType === "DDR5" ? 60 : 40;
      const sizeDiff = targetSize - baseSize;
      if (sizeDiff <= 0) return 0;
      // 每4GB一跳
      return (sizeDiff / 4) * pricePer4GB;
    },

    calcStorageDiff(baseSize, targetSize) {
      if (targetSize <= baseSize) return 0;

      const tiers = [
        { from: 256, to: 512, price: 30 },
        { from: 512, to: 1024, price: 100 },
        { from: 1024, to: 2048, price: 120 },
        { from: 2048, to: 4096, price: 180 },
        { from: 4096, to: 6144, price: 220 },
        { from: 6144, to: 8192, price: 180 }
      ];

      let total = 0;
      let current = baseSize;
      for (const tier of tiers) {
        if (current >= targetSize) break;
        if (current >= tier.from && current < tier.to) {
          total += tier.price;
          current = tier.to;
        }
      }
      return total;
    },

    formatStorage(sizeGb) {
      if (sizeGb >= 1024) {
        const tb = sizeGb / 1024;
        return Number.isInteger(tb) ? `${tb}TB` : `${tb.toFixed(0)}TB`;
      }
      return `${sizeGb}GB`;
    }
  }
};
</script>

<style scoped>
.about {
  min-height: 100vh;
  background-color: #f5f5f5;
}
.el-header {
  text-align: center;
  padding: 20px;
}
</style>
